import React from "react";
import LegalLayout, { Section, List } from "./LegalLayout";
import { RegulatoryList, LinksList, MediationText } from "./RegulatoryInfo";
import { PHONE, EMAIL, ADDRESS } from "@/lib/siteData";

const H3 = ({ children }) => <h3 className="font-semibold text-md-primary pt-2">{children}</h3>;
const Warn = ({ children }) => <p className="border-l-2 border-md-secondary pl-4 font-medium text-md-primary">{children}</p>;

export default function Cgu() {
  return (
    <LegalLayout eyebrow="Informations légales" title="Conditions générales d'utilisation" testId="cgu-page">
      <Section n="1" title="Objet">
        <p>
          Les présentes conditions générales d'utilisation (« CGU ») encadrent l'accès au site mdcourtage.fr (le « Site »)
          et son utilisation. Elles précisent notamment comment vous pouvez donner, consulter et retirer votre consentement à
          être contacté. En naviguant sur le Site, vous êtes invité à en prendre connaissance. Les prestations de courtage
          sont régies par des <a href="/cgv" className="underline text-md-primary">conditions générales de vente</a> (« CGV »)
          distinctes. <strong>Ni la navigation, ni l'acceptation des CGU ou des CGV ne valent consentement au démarchage</strong> :
          celui-ci est recueilli séparément (article 6).
        </p>
      </Section>

      <Section n="2" title="Éditeur et hébergeur du site">
        <List items={[
          "Éditeur : MD Courtage — Maël DUBOIS, entrepreneur individuel — SIRET 812 084 259 00025 — RCS Paris n° 812 084 259.",
          `Siège : ${ADDRESS}. Téléphone : ${PHONE}. Email : ${EMAIL}.`,
          "Directeur de la publication : Maël DUBOIS.",
          "Hébergeur : Hostinger International Ltd — 61 Lordou Vironos Street, 6023 Larnaca, Chypre — téléphone : +370 645 03378.",
        ]} />
      </Section>

      <Section n="3" title="Statuts réglementaires et liens avec les établissements">
        <RegulatoryList />
        <LinksList />
      </Section>

      <Section n="4" title="Accès au site">
        <p>
          Le Site est accessible gratuitement. MD Courtage est tenu à une simple obligation de moyens quant à sa
          disponibilité : l'accès peut être interrompu pour maintenance, mise à jour ou en cas de force majeure, sans que
          cela n'ouvre droit à indemnité.
        </p>
      </Section>

      <Section n="5" title="Services proposés">
        <H3>5.1 Simulateur de crédit</H3>
        <p>Les résultats sont indicatifs, non contractuels, et ne constituent ni une offre de prêt ni un engagement de financement.</p>
        <Warn>Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager.</Warn>
        <p>Aucun versement, de quelque nature que ce soit, ne peut être exigé d'un particulier avant l'obtention d'un ou plusieurs prêts d'argent.</p>
        <H3>5.2 Placements</H3>
        <Warn>Les placements présentent un risque de perte en capital. Les performances passées ne préjugent pas des performances futures.</Warn>
        <H3>5.3 Assistant conversationnel (intelligence artificielle)</H3>
        <p>
          Vous dialoguez avec un système d'intelligence artificielle, et non avec un conseiller humain. Ses réponses sont
          générées automatiquement, ont une portée générale et peuvent contenir des erreurs : elles ne constituent pas un
          conseil personnalisé. Ne lui communiquez pas de données sensibles (santé, coordonnées bancaires). Les conversations
          sont conservées au maximum 3 ans (voir la <a href="/confidentialite" className="underline text-md-primary">politique de confidentialité</a>).
        </p>
        <H3>5.4 Autres services</H3>
        <List items={[
          "Formulaires de demande de devis et de contact.",
          "Articles de conseil : informations générales, sans valeur de recommandation personnalisée.",
          "Prise de rendez-vous via Calendly, service tiers (États-Unis) accessible par un lien externe et soumis à ses propres conditions ; il n'est pas intégré au Site.",
        ]} />
      </Section>

      <Section n="6" title="Consentement à être contacté — démarchage">
        <p>
          Conformément à l'article L.223-1 du Code de la consommation, dans sa rédaction applicable à compter du 11 août 2026,
          <strong> MD Courtage ne vous démarche par téléphone qu'avec votre consentement préalable</strong>, libre, spécifique,
          éclairé, univoque et révocable. La prospection par email obéit à un régime distinct (article L.34-5 du Code des postes
          et des communications électroniques) et fait l'objet d'un consentement séparé.
        </p>
        <H3>6.1 Recueil du consentement</H3>
        <List items={[
          "Il est recueilli par des cases à cocher dédiées, jamais pré-cochées, distinctes de toute acceptation des CGU ou des CGV.",
          "Trois choix séparés et tous facultatifs vous sont proposés : (a) être rappelé par téléphone au sujet de votre demande ; (b) recevoir des offres commerciales par téléphone ; (c) recevoir des offres commerciales par email.",
          "Si vous ne cochez pas la case (a), votre demande est traitée de la même façon et vous recevez une réponse uniquement par email. Refuser le téléphone ne vous prive d'aucun service.",
        ]} />
        <H3>6.2 Informations délivrées avant votre accord</H3>
        <List items={[
          `Identité de l'appelant : MD Courtage — Maël DUBOIS (Orias n° 26006910), appels émis depuis le ${PHONE}.`,
          "Nature des services : crédit (immobilier, rachat, professionnel, consommation), assurance (emprunteur, prévoyance, santé, IARD, professionnelle) et placement (assurance-vie, PER, SCPI, épargne).",
          "Durée : 12 mois maximum à compter du recueil, sans renouvellement tacite. Un email vous propose de le renouveler 30 jours avant l'échéance ; sans action de votre part, il expire.",
          "Votre droit de retirer votre consentement à tout moment, simplement et gratuitement, et d'obtenir gratuitement sa preuve.",
        ]} />
        <H3>6.3 Preuve du consentement</H3>
        <p>
          Pour chaque accord, MD Courtage enregistre sur un support durable : la date et l'heure, le formulaire d'origine, le
          texte exact accepté et sa version, l'adresse IP et le navigateur utilisés, la date d'expiration et, le cas échéant, la
          date de retrait. Cette preuve est conservée <strong>3 ans à compter de la fin du consentement</strong> (expiration ou
          retrait), puis supprimée automatiquement. Elle vous est communiquée gratuitement via le lien personnel figurant dans
          l'email de confirmation, ou sur simple demande à {EMAIL}.
        </p>
        <H3>6.4 Retrait du consentement</H3>
        <List items={[
          "Par le lien personnel « Consulter la preuve ou retirer mon consentement » de chaque email de confirmation ;",
          <>Sur la page <a href="/consentement" className="underline text-md-primary">mdcourtage.fr/consentement</a>, avec votre adresse email ;</>,
          `Par email à ${EMAIL}, par téléphone au ${PHONE}, ou oralement lors d'un appel.`,
        ]} />
        <p>Le retrait est gratuit, sans justification, et prend effet sans délai.</p>
        <H3>6.5 Créneaux d'appel</H3>
        <p>Du lundi au vendredi, de 10h à 13h et de 14h à 20h, hors jours fériés, sauf créneau différent que vous avez expressément demandé.</p>
        <H3>6.6 Appels ne relevant pas du démarchage</H3>
        <List items={[
          "Appels non commerciaux : suivi d'un dossier ou d'un contrat dont vous êtes client, gestion d'un sinistre, demande de pièces, information réglementaire. Ils ne constituent pas une prospection.",
          "Rappel que vous avez sollicité : il n'a lieu que si vous avez coché la case (a) ou demandé expressément à être rappelé.",
          "Exception légale : sans consentement préalable, une sollicitation commerciale reste possible uniquement dans le cadre de l'exécution d'un contrat en cours et en rapport avec l'objet de ce contrat, y compris pour proposer des produits ou services afférents ou complémentaires, ou de nature à en améliorer les performances ou la qualité.",
        ]} />
        <p>
          Tout contrat conclu à la suite d'un démarchage téléphonique non conforme est nul. Le dispositif d'opposition Bloctel
          a pris fin avec l'entrée en vigueur de ce régime de consentement préalable.
        </p>
      </Section>

      <Section n="7" title="Propriété intellectuelle">
        <p>
          Les contenus du Site (textes, logo, marques, visuels, articles, simulateur, éléments graphiques) sont la propriété
          de MD Courtage ou de ses partenaires. Toute reproduction ou représentation, totale ou partielle, sans autorisation
          écrite préalable est interdite, de même que toute extraction automatisée de données (scraping, aspiration), y compris
          des résultats du simulateur (articles L.122-4 et L.342-1 du Code de la propriété intellectuelle). Le partage des
          articles via les boutons prévus à cet effet est autorisé.
        </p>
      </Section>

      <Section n="8" title="Responsabilité">
        <p>
          Les informations publiées sont fournies à titre indicatif et peuvent évoluer (taux, réglementation, fiscalité). Elles
          ne remplacent pas un conseil personnalisé, qui suppose la remise du document d'entrée en relation, le recueil de vos
          informations et, selon le cas, une lettre de mission. Elles n'exonèrent pas MD Courtage de ses obligations
          réglementaires dans le cadre d'une relation personnalisée. Dans les limites permises par la loi, MD Courtage n'est
          pas responsable du contenu des sites tiers accessibles par lien hypertexte ; aucune clause des présentes ne limite
          sa responsabilité en cas de faute lourde ou dolosive ou de dommage corporel.
        </p>
      </Section>

      <Section n="9" title="Données personnelles et cookies">
        <p>
          Seuls des éléments techniques strictement nécessaires (par exemple l'identifiant de session de l'assistant
          conversationnel ou la mémorisation de vos choix de cookies) sont déposés sans votre accord. Les traceurs publicitaires
          de Google Ads et de Meta (Facebook, Instagram), qui mesurent l'efficacité de nos publicités et permettent le
          reciblage, ne sont chargés <strong>qu'après votre consentement</strong> via le bandeau dédié, où « Tout refuser » est
          aussi simple que « Tout accepter ». Votre choix est conservé 6 mois et modifiable à tout moment via le lien « Gérer
          les cookies » en bas de page. Les polices sont hébergées sur nos serveurs et Calendly n'est accessible que par lien externe.
        </p>
        <p>
          Vous disposez de droits d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité sur
          vos données, ainsi que du droit d'introduire une réclamation auprès de la CNIL (www.cnil.fr). Le détail figure dans
          la <a href="/confidentialite" className="underline text-md-primary">politique de confidentialité</a>.
        </p>
      </Section>

      <Section n="10" title="Versions, droit applicable et litiges">
        <p>
          Les présentes CGU sont datées et versionnées (version indiquée en tête de page). Les versions antérieures sont
          archivées et communiquées sur simple demande. Les consentements déjà recueillis restent soumis au texte accepté à
          leur date de recueil.
        </p>
        <p>Les présentes CGU sont soumises au droit français. En cas de litige, une solution amiable est recherchée en priorité.</p>
        <MediationText />
        <p>
          À défaut d'accord, le consommateur peut saisir, à son choix, la juridiction du lieu où il demeurait au moment de la
          conclusion du contrat ou de la survenance du fait dommageable, ou toute autre juridiction compétente selon la loi
          (article R.631-3 du Code de la consommation). Pour les utilisateurs professionnels, compétence est attribuée aux
          tribunaux de Paris.
        </p>
      </Section>
    </LegalLayout>
  );
}
